import { useEffect, useRef, useState, useMemo } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, TouchableOpacity, ScrollView, Pressable } from "react-native";
import { GreenCircle, RightArrow, LeftArrow, Assessment } from "../components/svgs/common/index";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import {
  useGetAvailableCoursesForStudentQuery,
  useGetCourseByIdQuery,
  useGetDueSoonActivitiesBySchoolAndUserQuery,
  useGetEnrolledCoursesForStudentQuery,
  useGetUserPermissionsQuery,
} from "../graphql/generated/graphql";
import Loader from "../components/common/status/Loader/Loader";
import TimeLeftSmall from "../components/svgs/common/TimeLeftSmall";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { MotiView } from "moti";
import { useAppConfig } from "../AppConfigProvider";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useCourseDetails } from "../contexts/CourseDetailsContext";
import DefaultLayout from "../layouts/DefaultLayout.web";
import AiTutorCardProgress from "../components/lms/AiTutorCardProgress/AiTutorCardProgress";
import AxioResourceCard from "../components/customCoursework/AxioResourceCard/AxioResourceCard";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";
import { RolePermissions } from "../constants/rolesAndPermissions";
import CatalogueCard from "../components/customCoursework/CourseCatalogue/CatalogueCard/CatalogueCard";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { FileText, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../hooks/useAuth";

const getActivityIcon = (activityType) => {
  if (activityType === "aitutor") {
    return <Sparkle width={24} height={24} fill="#3E68FE" />;
  } else if (activityType === "assignment") {
    return <FileText width={24} height={24} fill="#3E68FE" />;
  }
  return null;
};

function HomeView() {
  const [containerWidth, setContainerWidth] = useState(0);

  const cardWidth = useMemo(() => {
    if (containerWidth === 0) return 400; // fallback
    return Math.max(400, (containerWidth - 32) / 3); // 32 = 2 gaps (16px each)
  }, [containerWidth]);

  const scrollStepSize = useMemo(() => {
    return cardWidth + 16; // card width + gap
  }, [cardWidth]);

  const { meData } = useAuth();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const { isMedium } = useResponsiveScreen();

  const userName = meData?.me?.name || "";
  const posthog = usePostHog();
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const integrationId = appConfig.axioSchools[0];
  const scrollViewRef = useRef(null);
  const { setActiveResource } = useActiveCourse();

  const [showAllActivities, setShowAllActivities] = useState(false);
  const [hoveredActivityId, setHoveredActivityId] = useState(null);
  const { data: userPermissionsData } = useGetUserPermissionsQuery();
  const userPermissions = userPermissionsData?.getUserPermissions || [];
  const isTeacherOrAdmin =
    userPermissions.includes(RolePermissions.analyticsViewFaculty) ||
    userPermissions.includes(RolePermissions.analyticsViewSchool) ||
    userPermissions.includes(RolePermissions.wildcard);

  const handleActivityHoverIn = (activityId) => {
    setHoveredActivityId(activityId);
  };

  const handleActivityHoverOut = () => {
    setHoveredActivityId(null);
  };

  const { setIsVisible, setCourseData, onEnroll, loading } = useCourseDetails();

  useEffect(() => {
    if (isCompanionMode) {
      navigation.navigate("Companion Home");
    }
  }, [isCompanionMode, navigation]);

  useEffect(() => {
    posthog?.capture("Course Catalogue Page Visited");
  }, []);

  const { data: courses } = useGetEnrolledCoursesForStudentQuery({
    variables: {
      schoolId: integrationId,
      page: 1,
      pageSize: 10,
    },
    skip: !integrationId,
  });

  const nonCompletedCourses = useMemo(() => {
    return courses?.getEnrolledCoursesForStudent?.data?.courses?.filter((course) => course.progress < 100) || [];
  }, [courses?.getEnrolledCoursesForStudent?.data?.courses]);

  const { data: dueSoonData, loading: dueSoonLoading } = useGetDueSoonActivitiesBySchoolAndUserQuery({
    variables: {
      schoolId: integrationId,
    },
    skip: !integrationId || isTeacherOrAdmin,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  const activitiesDueSoon = useMemo(() => {
    if (isTeacherOrAdmin) {
      return [];
    }
    const courses = dueSoonData?.getDueSoonActivitiesBySchoolAndUser?.data || [];
    const activities = [];

    courses.forEach((course) => {
      course.modules?.forEach((module) => {
        module.assignments?.forEach((assignment) => {
          const isCompleted = assignment.submissions?.some((sub) => sub.isCompleted);

          activities.push({
            id: assignment.id,
            type: "assignment",
            title: assignment.name,
            description: assignment.description,
            dueDate: assignment.due_date,
            courseName: course.name,
            courseId: course.id,
            moduleTitle: module.title,
            isCompleted,
            completionStatus: isCompleted ? "Completed" : "Not Started",
          });
        });

        module.aiTutors?.forEach((aiTutor) => {
          const totalObjectives = aiTutor.objectives?.length || 0;
          const completedObjectives =
            aiTutor.objectives?.filter((objective) =>
              objective.userHasProgressConnection?.edges?.some((edge) => edge.properties?.status === "completed")
            ).length || 0;

          const isCompleted = totalObjectives > 0 && completedObjectives === totalObjectives;
          const completionStatus =
            totalObjectives === 0 ? "No Objectives" : `${completedObjectives}/${totalObjectives} Objectives Complete`;

          activities.push({
            id: aiTutor.id,
            type: "aitutor",
            title: aiTutor.title,
            description: aiTutor.description,
            dueDate: aiTutor.dueDate,
            courseName: course.name,
            courseId: course.id,
            moduleTitle: module.title,
            isCompleted,
            completionStatus,
          });
        });
      });
    });

    activities.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    return activities;
  }, [dueSoonData]);

  const [selectedCourseId, setSelectedCourseId] = useState(null);

  useEffect(() => {
    if (nonCompletedCourses.length > 0 && nonCompletedCourses[0]?.course?.id) {
      setSelectedCourseId(nonCompletedCourses[0]?.course?.id);
    }
  }, [nonCompletedCourses]);

  const { data: courseDetails, loading: courseLoading } = useGetCourseByIdQuery({
    variables: { id: selectedCourseId, withModules: true },
    skip: !selectedCourseId,
  });

  const firstModuleName = courseDetails?.getCourseById?.data?.[0]?.modules?.[0]?.title || "Continue your course";

  const titles = [];

  const { data: recommendedCoursesData, loading: recommendedCoursesLoading } = useGetAvailableCoursesForStudentQuery({
    variables: {
      schoolId: integrationId,
      page: 1,
      pageSize: 2,
    },
    skip: !integrationId,
  });

  const recommendedCourses = useMemo(() => {
    return (
      recommendedCoursesData?.getAvailableCoursesForStudent?.data?.courses
        ?.filter((course) => {
          const enrolledStudents = course.course.enrolledStudents;
          const isEnrolled = enrolledStudents.some((student) => student.id === meData?.me?.id);
          return !isEnrolled;
        })
        .slice(0, 2) || []
    );
  }, [recommendedCoursesData?.getAvailableCoursesForStudent?.data?.courses, meData?.me?.id]);

  const handleContinueCoursePress = (id) => {
    navigation.navigate("Custom Course Student", {
      courseId: id,
      integration_id: integrationId,
    });
  };

  const handleActivityPress = (activity) => {
    if (activity.type === "assignment") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_ASSIGNMENT, resourceId: activity.id });
      navigation.navigate("Custom Assignment Student", {
        resource_type: "assignment",
        resource_id: activity.id,
        course_id: activity.courseId,
        integration_id: integrationId,
      });
    } else if (activity.type === "aitutor") {
      setActiveResource({
        resourceType: ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE,
        resourceId: activity.id,
      });
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: activity.id,
        course_id: activity.courseId,
        integration_id: integrationId,
      });
    }
  };

  const scrollLeft = () => {
    const currentScrollPosition = (scrollViewRef.current as ScrollView).getScrollableNode().scrollLeft;
    (scrollViewRef.current as ScrollView).scrollTo({ x: currentScrollPosition - scrollStepSize, animated: true });
  };

  const scrollRight = () => {
    const currentScrollPosition = (scrollViewRef.current as ScrollView).getScrollableNode().scrollLeft;
    (scrollViewRef.current as ScrollView).scrollTo({ x: currentScrollPosition + scrollStepSize, animated: true });
  };

  const handleEnrollPress = (courseId, hasCost) => {
    onEnroll(courseId, hasCost, () => {
      navigation.navigate("Custom School Student Courses", {
        integration_id: integrationId,
      });
    });
  };

  const handleDetailsPress = (course, name, preRequisitesCompleted) => {
    setCourseData({
      ...course,
      name,
      preRequisitesCompleted,
      modules: course?.modules?.map((module) => ({
        name: module.title,
        activities: [
          ...(module?.assignments?.map((assignment) => ({
            name: assignment.name,
            id: assignment.id,
          })) || []),
          ...(module?.aiTutors?.map((aiTutor) => ({
            name: aiTutor.title,
            id: aiTutor.id,
          })) || []),
          ...(module?.axioResources?.map((resource) => ({
            name: resource.name,
            id: resource.id,
          })) || []),
        ],
      })),
    });
    setIsVisible(true);
  };

  const displayedActivities = showAllActivities ? activitiesDueSoon : activitiesDueSoon.slice(0, 3);

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        // @ts-expect-error - transition is not typed
        transition={{ type: "timing", duration: 500 }}>
        <View style={{ flexDirection: "column", justifyContent: "flex-start" }}>
          <View
            style={{
              flexDirection: isMedium ? "row" : "column",
              justifyContent: isMedium ? "space-between" : "flex-start",
              alignItems: isMedium ? "center" : "flex-start",
              gap: 8,
            }}>
            <CustomText
              text={"Welcome back, " + userName}
              size={isMedium ? "m" : "xs"}
              useTranslationText={false}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={{ color: "#101828" }}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Here’s what’s happening in your learning journey today"
            size="m"
            weight="normal"
            style={{ color: "#667085" }}
          />
        </View>
      </MotiView>
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />
      {nonCompletedCourses.length > 0 && (
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          delay={10}
          // @ts-expect-error - transition is not typed
          transition={{ type: "timing", duration: 500 }}>
          <View
            style={[
              styles.engagementContainer,
              { flexDirection: isMedium ? "row" : "column", alignItems: isMedium ? "center" : "flex-start", gap: 20 },
            ]}>
            <View style={{ flex: 1, flexDirection: "column", gap: 8 }}>
              <View
                style={{
                  paddingTop: 8,
                  flexDirection: isMedium ? "row" : "column",
                  alignItems: isMedium ? "center" : "flex-start",
                  gap: 8,
                  flexWrap: "nowrap",
                }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "nowrap",
                  }}>
                  <GreenCircle width={8} height={8} color="#30B83D" style={{ marginTop: 2 }} />
                  <CustomText
                    useTranslationText={false}
                    textType="text"
                    text="CONTINUE LEARNING"
                    size="s"
                    weight="bold"
                    style={{ color: "#30B83D", textTransform: "uppercase" }}
                  />
                </View>
                {isMedium && (
                  <View style={{ height: 12, borderLeftWidth: 1, borderLeftColor: "#D0D5DD", marginHorizontal: 4 }} />
                )}
                <CustomText
                  useTranslationText={false}
                  textType="text"
                  text={nonCompletedCourses[0]?.course?.name || "Latest Course"}
                  size="xs"
                  style={{ color: "#667085", letterSpacing: 1.26, textTransform: "uppercase" }}
                />
              </View>

              <CustomText
                useTranslationText={false}
                textType="text"
                text={courseLoading ? "Loading module..." : firstModuleName}
                size="xxl"
                weight="ultraBold"
                style={{ color: "#101828" }}
              />

              <View>
                <View
                  style={{
                    paddingTop: 8,
                    flexDirection: isMedium ? "row" : "column",
                    alignItems: isMedium ? "center" : "flex-start",
                    gap: 12,
                    width: "100%",
                  }}>
                  <View style={{ width: 200, height: 8, backgroundColor: "#E4E7EC", borderRadius: 4 }}>
                    <View
                      style={{
                        width: `${nonCompletedCourses[0]?.progress || 0}%`,
                        height: 8,
                        backgroundColor: "#3E68FE",
                        borderRadius: 4,
                      }}
                    />
                  </View>

                  <CustomText
                    useTranslationText={false}
                    textType="text"
                    text={`${nonCompletedCourses[0]?.progress || 0}% Complete`}
                    size="s"
                    style={{ color: "#667085" }}
                  />

                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 4,
                      flexWrap: "nowrap",
                    }}>
                    <TimeLeftSmall size={16} color="#667085" />
                    <CustomText
                      useTranslationText={false}
                      textType="text"
                      text="About 1 hour left"
                      size="s"
                      style={{ color: "#667085" }}
                    />
                  </View>
                </View>
              </View>
            </View>
            <CustomButton
              text="Resume Activity"
              size="s"
              style={{
                paddingTop: 8,
                paddingRight: 12,
                paddingBottom: 8,
                paddingLeft: 12,
              }}
              styleType="primary"
              onPress={() => {
                handleContinueCoursePress(nonCompletedCourses[0]?.course?.id);
              }}
            />
          </View>
        </MotiView>
      )}

      <CustomSpacing type="vertical" size="xl" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`My Courses (${courses?.getEnrolledCoursesForStudent?.data?.courses?.length})`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
          aria-level="2"
          role="heading"
        />
        <View style={{ flexDirection: "row", alignItems: "center", gap: 9 }}>
          <TouchableOpacity
            style={{
              padding: 9,
              backgroundColor: "#F2F4F7",
              borderRadius: 25,
            }}
            onPress={scrollLeft}>
            <LeftArrow color="#111111" />
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              padding: 9,
              backgroundColor: "#F2F4F7",
              borderRadius: 25,
            }}
            onPress={scrollRight}>
            <RightArrow color="#111111" />
          </TouchableOpacity>
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      <View
        style={{ width: "100%", overflow: "hidden" }}
        onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}>
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          pagingEnabled={false}
          scrollEventThrottle={16}
          contentContainerStyle={styles.flexWebContainer}>
          {courses?.getEnrolledCoursesForStudent?.data?.courses?.map((course) => (
            <CatalogueCard
              key={course.course.id}
              course={course.course as any}
              onEnrollPress={() => handleEnrollPress(course.course?.id, !!course.course?.cost)}
              onDetailsPress={handleDetailsPress}
              isLoading={false}
              progress={course.progress}
              handleContinueCoursePress={() => handleContinueCoursePress(course.course?.id)}
              cardWidth={cardWidth}
            />
          ))}
        </ScrollView>
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {/* Due Soon Activities Section */}
      {!isTeacherOrAdmin && (
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          delay={100}
          // @ts-expect-error - transition is not typed
          transition={{ type: "timing", duration: 500 }}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
            <CustomText
              useTranslationText={false}
              text={`Activities due Soon (${activitiesDueSoon.length})`}
              size="l"
              weight="bold"
              style={{ color: "#101828" }}
              aria-level="2"
              role="heading"
            />
            {activitiesDueSoon.length > 3 && (
              <TouchableOpacity onPress={() => setShowAllActivities(!showAllActivities)}>
                <CustomText
                  useTranslationText={false}
                  text={showAllActivities ? "Show Less" : "View All"}
                  size="m"
                  style={{ color: "#3E68FE" }}
                />
              </TouchableOpacity>
            )}
          </View>

          <CustomSpacing type="vertical" size="l" />

          {dueSoonLoading ? (
            <View style={{ alignItems: "center", justifyContent: "center", padding: 40 }}>
              <Loader />
            </View>
          ) : activitiesDueSoon.length === 0 ? (
            <View style={styles.engagementContainer}>
              <CustomText
                useTranslationText={false}
                text="No activities due in the next 7 days"
                size="m"
                weight="bold"
                style={{ color: "#667085", textAlign: "center", width: "100%" }}
              />
            </View>
          ) : (
            <View>
              {displayedActivities.map((activity) => {
                const activityKey = `${activity.type}-${activity.id}`;
                const isHovered = hoveredActivityId === activityKey;
                const icon = getActivityIcon(activity.type);
                if (activity.type === "aitutor") {
                  const totalObjectives = activity.completionStatus.includes("/")
                    ? parseInt(activity.completionStatus.split("/")[1])
                    : 0;
                  const completedObjectives = activity.completionStatus.includes("/")
                    ? parseInt(activity.completionStatus.split("/")[0])
                    : 0;

                  return (
                    <AiTutorCardProgress
                      key={activityKey}
                      icon={icon}
                      title={activity.title}
                      isHovered={isHovered}
                      isComplete={activity.isCompleted}
                      onPress={() => handleActivityPress(activity)}
                      onHoverIn={() => handleActivityHoverIn(activityKey)}
                      onHoverOut={handleActivityHoverOut}
                      totalObjectives={totalObjectives}
                      completedObjectives={completedObjectives}
                      dueDate={activity.dueDate}
                      isReviewContentBeingGenerated={false}
                      activityState={null}
                      scheduledDate={null}
                    />
                  );
                } else if (activity.type === "assignment") {
                  return (
                    <AxioResourceCard
                      key={activityKey}
                      title={activity.title}
                      icon={icon}
                      description={activity.description}
                      videoUrl={null}
                      files={[]}
                      isHovered={isHovered}
                      onPress={() => handleActivityPress(activity)}
                      onHoverIn={() => handleActivityHoverIn(activityKey)}
                      onHoverOut={handleActivityHoverOut}
                      dueDate={activity.dueDate}
                      activityState={null}
                      scheduledDate={null}
                      isPending={false}
                      isComplete={false}
                      grade={null}
                    />
                  );
                }

                return null;
              })}
            </View>
          )}
        </MotiView>
      )}

      <CustomSpacing type="vertical" size="l" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`Recent Grades`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
          aria-level="2"
          role="heading"
        />
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}></View>
      </View>

      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        // @ts-expect-error - transition is not typed
        transition={{ type: "timing", duration: 500 }}>
        {titles.length === 0 ? (
          <View style={styles.engagementContainer}>
            <CustomText
              useTranslationText={false}
              text="No Grades Available"
              size="m"
              weight="bold"
              style={{ color: "#667085", textAlign: "center", width: "100%" }}
            />
          </View>
        ) : (
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              gap: 7,
            }}>
            {titles.map((title, index) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  padding: 20,
                  gap: 12,
                  marginTop: 24,
                  flex: 1,
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: "#EAECF0",
                  backgroundColor: "#FFF",
                  overflow: "hidden",
                }}>
                <View style={{ flexDirection: "row", gap: 12 }}>
                  <Assessment />
                  <View style={{ flexDirection: "column", paddingRight: 20 }}>
                    <CustomText
                      useTranslationText={false}
                      text={title}
                      size="s"
                      weight="bold"
                      style={{ color: "#101828" }}
                    />
                    <CustomText
                      useTranslationText={false}
                      text={`Name of Course`}
                      size="xs"
                      style={{ color: "#667085" }}
                    />
                  </View>
                </View>
                <View style={{ alignSelf: "center", backgroundColor: "#3E68FE", padding: 5, borderRadius: 25 }}>
                  <CustomText
                    useTranslationText={false}
                    weight="ultraBold"
                    style={{ color: "white" }}
                    size="s"
                    text="32/100"
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </MotiView>

      <CustomSpacing type="vertical" size="l" />
      <CustomSpacing type="vertical" size="l" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`Recommended Courses`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
          aria-level="2"
          role="heading"
        />
        <Pressable
          onPress={() => navigation.navigate("Custom School Student Courses")}
          style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
          <CustomText useTranslationText={false} text="View All" size="m" style={{ color: "#667085" }} />
        </Pressable>
      </View>
      <CustomSpacing type="vertical" size="l" />
      {recommendedCoursesLoading ? (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <Loader />
        </View>
      ) : recommendedCourses.length === 0 ? (
        <View style={styles.engagementContainer}>
          <CustomText
            useTranslationText={false}
            text="No Recommended Courses Available"
            size="m"
            weight="bold"
            style={{ color: "#667085", textAlign: "center", width: "100%" }}
          />
        </View>
      ) : (
        <View style={{ width: "100%", overflow: "hidden" }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            pagingEnabled={false}
            scrollEventThrottle={16}
            contentContainerStyle={isMedium ? styles.flexWebContainer : styles.flexContainer}>
            {recommendedCourses.map((course) => (
              <CatalogueCard
                key={course.course?.id}
                course={course.course as any}
                onEnrollPress={() => handleEnrollPress(course.course?.id, !!course.course?.cost)}
                onDetailsPress={handleDetailsPress}
                isLoading={false}
                progress={course.progress}
                handleContinueCoursePress={() => handleContinueCoursePress(course.course?.id)}
                cardWidth={cardWidth}
              />
            ))}
          </ScrollView>
        </View>
      )}
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  engagementContainer: {
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    padding: 20,
    justifyContent: "space-between",
    marginTop: 24,
    marginBottom: 31,
    flex: 1,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFF",
    overflow: "hidden",
  },
  flexWebContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 16,
    paddingHorizontal: 0,
  },
  flexContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
});

export default HomeView;
